import { useTheme } from '@material-ui/core';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Edge, useEdgesState } from 'reactflow';
import {
  GetSystemErrorSeverity,
  GraphLayout,
  GraphNode,
  Group,
  SearchState,
} from '../../../../types';
import { HeatmapLayerConfig } from '../components/HeatmapConfig';
import { DependencyGraphNode } from '../nodes/types';

interface Layout {
  nodes: DependencyGraphNode[];
  idToNodeMap: Map<
    string,
    {
      x: number;
      y: number;
      r: number;
    }
  >;
  taxonomies: GraphLayout<GraphNode>;
}

type DependencyGraphRF = {
  edges: Edge<any>[];
  setEdges: Dispatch<SetStateAction<Edge<any>[]>>;

  layout: Layout;

  focusedGroups: Set<string>;
  setFocusedGroups: Dispatch<SetStateAction<Set<string>>>;

  selectedSystem: string | undefined;
  setSelectedSystem: Dispatch<SetStateAction<string | undefined>>;

  onSelectedGroupChange: (group: Group | null) => void;

  getSystemErrorSeverity?: GetSystemErrorSeverity;

  searchState?: SearchState;

  showErrors?: boolean;
  setShowErrors?: Dispatch<SetStateAction<boolean>>;

  heatmapConfig?: HeatmapLayerConfig;
  setHeatmapConfig: Dispatch<SetStateAction<HeatmapLayerConfig | undefined>>;
  heatmapLayersConfig: HeatmapLayerConfig[];
  defaultColors: [string, string];
};

const defaultDependencyGraphRFContext = null;

export const DependencyGraphRFContext = createContext<DependencyGraphRF | null>(
  defaultDependencyGraphRFContext,
);

export const useDependencyGraphRFContext = () => {
  const contextValue = useContext(DependencyGraphRFContext);

  if (!contextValue) {
    throw new Error(
      'useDependencyGraphRFContext has to be used within <DependencyGraphRFContext.Provider>',
    );
  }

  return contextValue;
};

export const DependencyGraphRFContextProvider: FC<{
  children: ReactNode;
  layout: Layout;
  searchState?: SearchState;
  showErrors?: boolean;
  setShowErrors?: Dispatch<SetStateAction<boolean>>;
  getSystemErrorSeverity?: GetSystemErrorSeverity;
  onSelectedGroupChange: (group: Group | null) => void;
  heatmapLayersConfig: HeatmapLayerConfig[];
}> = ({
  children,
  layout,
  getSystemErrorSeverity,
  searchState,
  showErrors,
  setShowErrors,
  onSelectedGroupChange,
  heatmapLayersConfig,
}) => {
  const theme = useTheme();
  const [selectedSystem, setSelectedSystem] = useState<string | undefined>(
    undefined,
  );
  const [focusedGroups, setFocusedGroups] = useState(new Set<string>());
  const [edges, setEdges] = useEdgesState([]);

  const [heatmapConfig, setHeatmapConfig] = useState<
    HeatmapLayerConfig | undefined
  >();

  const defaultColors: [string, string] = useMemo(
    () => [
      theme.palette.marketplaceGraph.heatmapRange.light,
      theme.palette.marketplaceGraph.heatmapRange.dark,
    ],
    [theme],
  );

  // important that we memoize this since many graph nodes consume this context directly, when value changes they will always rerender
  const value = useMemo(
    () => ({
      edges,
      setEdges,
      layout,
      focusedGroups,
      setFocusedGroups,
      selectedSystem,
      showErrors,
      setShowErrors,
      searchState,
      setSelectedSystem,
      onSelectedGroupChange,
      getSystemErrorSeverity,
      heatmapConfig,
      setHeatmapConfig,
      heatmapLayersConfig,
      defaultColors,
    }),
    [
      edges,
      setEdges,
      layout,
      focusedGroups,
      setFocusedGroups,
      selectedSystem,
      showErrors,
      setShowErrors,
      searchState,
      setSelectedSystem,
      onSelectedGroupChange,
      getSystemErrorSeverity,
      heatmapConfig,
      setHeatmapConfig,
      heatmapLayersConfig,
      defaultColors,
    ],
  );

  return (
    <DependencyGraphRFContext.Provider value={value}>
      {children}
    </DependencyGraphRFContext.Provider>
  );
};
